import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useHomepageStats } from "@/lib/useHomepageStats";
import { Search, GraduationCap, MapPin, Globe, ArrowUpRight, X } from "lucide-react";

export default function Partnerships() {
  const stats = useHomepageStats();
  const [homes, setHomes] = useState([]);
  const [home, setHome] = useState("");
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState(null); // {partnerships, grouped, count, countries}
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/partnerships/home-universities").then(r => setHomes(r.data.home_universities)).catch(()=>{});
  }, []);

  // instant search — every time home/country/q changes
  useEffect(() => {
    if (!home) { setState(null); return; }
    setLoading(true);
    const params = new URLSearchParams({ home });
    if (country) params.set("country", country);
    if (q) params.set("q", q);
    api.get(`/partnerships/search?${params}`)
      .then(r => setState(r.data))
      .catch(() => setState(null))
      .finally(() => setLoading(false));
  }, [home, country, q]);

  const selectedHome = useMemo(() => homes.find(h => h.short === home), [homes, home]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 md:pt-20 pb-24">
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-muted font-bold">Partnerships · instant search</div>
        <h1 className="mt-3 text-5xl md:text-6xl font-bold tracking-[-0.04em] leading-[0.95]">
          Pick your school.<br/>
          <span className="gradient-underline">See every partner.</span>
        </h1>
        <p className="mt-5 text-lg text-muted">
          {stats
            ? `${stats.partner_universities} partner universities across ${stats.countries} European countries`
            : "Partner universities across European countries"}. Sourced from each Indian B-school's own international office.
        </p>
      </div>

      {/* SEARCH BAR */}
      <div className="mt-12 moe-card p-5 md:p-6">
        <div className="grid md:grid-cols-12 gap-3">
          <div className="md:col-span-6 moe-field">
            <select data-testid="partnerships-home-select" value={home} onChange={(e) => setHome(e.target.value)}>
              <option value="">Select your home university…</option>
              {homes.map(h => (
                <option key={h.short} value={h.short}>{h.short}. {h.count} partners across {h.countries} countries</option>
              ))}
            </select>
            <label>Home university</label>
          </div>
          <div className="md:col-span-3 moe-field">
            <select data-testid="partnerships-country-select" value={country} onChange={(e) => setCountry(e.target.value)} disabled={!state}>
              <option value="">All countries</option>
              {(state?.countries || []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <label>Country</label>
          </div>
          <div className="md:col-span-3 moe-field relative">
            <input
              data-testid="partnerships-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder=" "
              disabled={!state}
            />
            <label>Search partner name</label>
            {q && (
              <button onClick={() => setQ("")} className="absolute right-2 top-3 p-1 text-muted hover:text-ink">
                <X size={14}/>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* EMPTY STATE */}
      {!home && (
        <div className="mt-16 text-center">
          <Search size={40} className="mx-auto text-muted moe-float" strokeWidth={1.4}/>
          <div className="mt-4 text-lg text-muted">Start by selecting your home university above.</div>
        </div>
      )}

      {/* LOADING */}
      {home && loading && (
        <div className="mt-8 space-y-3">
          {Array.from({length:5}).map((_,i)=><div key={i} className="moe-shimmer h-16"/>)}
        </div>
      )}

      {/* RESULTS */}
      {home && !loading && state && (
        <>
          {/* summary */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="moe-panel-mint p-5">
              <div className="text-[10px] uppercase tracking-widest font-bold">Total partners</div>
              <div className="mt-2 text-4xl font-bold tabular-nums">{state.count}</div>
            </div>
            <div className="moe-panel-cyan p-5">
              <div className="text-[10px] uppercase tracking-widest font-bold">Countries</div>
              <div className="mt-2 text-4xl font-bold tabular-nums">{state.countries.length}</div>
            </div>
            <div className="moe-panel-yolk p-5">
              <div className="text-[10px] uppercase tracking-widest font-bold">Home city</div>
              <div className="mt-2 text-lg font-bold">{selectedHome?.city || "–"}</div>
            </div>
            <div className="moe-panel-pink p-5">
              <div className="text-[10px] uppercase tracking-widest font-bold">Home university</div>
              <div className="mt-2 text-lg font-bold leading-tight">{selectedHome?.short}</div>
            </div>
          </div>

          {/* countries as filter chips */}
          {state.countries.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              <button onClick={() => setCountry("")} className={`moe-chip ${!country ? 'moe-chip-elec' : ''}`}>All · {state.count}</button>
              {state.countries.map(c => {
                const n = state.grouped[c]?.length || 0;
                const active = country === c;
                return (
                  <button
                    key={c}
                    onClick={() => setCountry(active ? "" : c)}
                    data-testid={`partnerships-country-chip-${c}`}
                    className={`moe-chip ${active ? 'moe-chip-elec' : 'hover:bg-black/5'}`}
                  >
                    {c} · {n}
                  </button>
                );
              })}
            </div>
          )}

          {/* rows grouped by country */}
          {state.count === 0 ? (
            <div className="mt-12 moe-card p-10 text-center text-muted">No partnerships match your filter.</div>
          ) : (
            <div className="mt-10 space-y-10">
              {state.countries.map(c => (
                <div key={c}>
                  <div className="flex items-baseline justify-between border-b border-ink pb-3 mb-4">
                    <div className="flex items-center gap-2">
                      <Globe size={16} className="text-elec"/>
                      <h2 className="text-2xl font-bold">{c}</h2>
                      <span className="text-xs text-muted">· {state.grouped[c].length} partner{state.grouped[c].length === 1 ? "" : "s"}</span>
                    </div>
                    <Link to={`/countries/${encodeURIComponent(c)}`} className="text-xs font-semibold text-elec hover:underline">
                      {c} visa dossier →
                    </Link>
                  </div>
                  <ul className="grid md:grid-cols-2 gap-3 moe-stagger">
                    {state.grouped[c].map(p => (
                      <li key={p.id} data-testid={`partnership-${p.id}`} className="moe-card p-4 moe-hover-lift">
                        <div className="flex items-start gap-3">
                          <GraduationCap size={18} className="text-elec shrink-0 mt-0.5" strokeWidth={1.8}/>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-ink leading-tight">{p.exchange_partner}</div>
                            <div className="mt-1 flex items-center gap-1 text-xs text-muted">
                              <MapPin size={11}/> {p.city}, {p.country}
                            </div>
                            <div className="mt-1 text-[11px] text-muted">{p.region}</div>
                            <div className="mt-2 moe-chip text-[11px]">{p.programme_type}</div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
